import './PageSetupWizard.scss';

import { TemplateType } from '@components/Common/CommonEnums';
import { useAurigaContext } from '@context/AurigaContext';
import { usePageSetupContext } from '@context/PageSetupContext';
import { useTOCContext } from '@context/TOCContext';
import AsyncSaveButton from '@deckManagement/components/Common/AsyncSaveButton/AsyncSaveButton';
// Step Components (add additional here)
import PageDetails from '@deckManagement/components/PageSetupWizard/components/PageDetails';
import PageDriverStep from '@deckManagement/components/PageSetupWizard/components/PageDriverStep';
import TableConfiguration from '@deckManagement/components/PageSetupWizard/components/TableConfiguration';
import useRecordLoadComplete from '@hooks/useRecordLoadComplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import { isEmpty, isNil, map, some } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageSetupSteps } from 'types/PageSetupContext.types';

const PageSetupWizard = () => {
  const {
    pageSetupSteps,
    currPageName,
    currDeckId,
    currentStep,
    nextStep,
    setCurrentStep,
    tocId,
    tocEntry,
    pageName,
    tocName,
    isPageCreated,
    setIsPageCreated,
    templateType,
    selectedTemplate,
    grainTemplates,
    grainMappings,
    grainBlockHeaders,
    reportingPeriods,
    sectionTemplateConfigs,
    templateCRUDItems,
    hasTableConfigChanged,
    snapshotTableConfig,
    resetTableConfig,
    isUpdateTemplate,
    setIsUpdateTemplate,
    savePageDrivers,
    pageDriversSaving,
    isDecklinesLoading,
    pageLockStatus,
  } = usePageSetupContext();
  const { updateTocEntry, saveChanges, deletePage, saveTemplate } = useTOCContext();
  const { toastNotification } = useAurigaContext();
  const navigate = useNavigate();
  useRecordLoadComplete(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  // Ref used to determine the need for Page Details saving on creation or updates
  const pendingPageDetailsSaveRef = useRef(false);
  // Ref used to trigger a deferred discard (delete) save
  const pendingDiscardRef = useRef(false);

  // To track the New Page workflow (versus Edit Page mode) in render
  const isNewPageWorkflow = isNil(currPageName) || isEmpty(currPageName);
  // To track whether we are on the last Step, and need to render the Save Page button
  const isLastStep = useMemo(
    () => (!isNil(currentStep) ? pageSetupSteps.indexOf(currentStep) === pageSetupSteps.length - 1 : false),
    [currentStep, pageSetupSteps],
  );
  // Numeric conversion of Step string identifier from enum for use in Stepper
  const currStepIndex = useMemo(
    () => (!isNil(currentStep) ? pageSetupSteps.indexOf(currentStep) : 0),
    [currentStep, pageSetupSteps],
  );

  /**
   * Controls the DISCARD action.
   * - New Page flow: deletes the TOC entry (whether persisted or not) and navigates back to the Page Library.
   * - Edit Page flow: navigates back to the Page view. Any changes saved during step progression are retained.
   */
  const handleDiscard = useCallback(() => {
    if (isNewPageWorkflow) {
      if (!tocId) return;
      deletePage(tocId);
      if (isPageCreated) {
        setIsDiscarding(true);
        pendingDiscardRef.current = true;
      } else {
        navigate(`/deck-preparation/decks/${currDeckId}/toc`);
      }
    } else if (selectedTemplate || !isEmpty(grainTemplates)) {
      // Only navigate to page view if templates actually have been set up and exist; otherwise, return to Page Library
      navigate(`/deck-preparation/decks/${currDeckId}/pages/${encodeURIComponent(pageName)}`);
    } else {
      navigate(`/deck-preparation/decks/${currDeckId}/toc`);
    }
  }, [
    isNewPageWorkflow,
    tocId,
    deletePage,
    isPageCreated,
    navigate,
    currDeckId,
    pageName,
    selectedTemplate,
    grainTemplates,
  ]);

  /**
   * Controls the NEXT step progression click.
   * Validates current step before advancing to the next step.
   * Results in updates to Current and Next steps in the PageSetupContext
   */
  const handleNextStep = useCallback(() => {
    if (isNil(nextStep)) return;

    // Validate Table Configuration step before advancing
    if (currentStep === PageSetupSteps.TABLE_CONFIGURATION) {
      // Validate block headers defined do not contain any empty selections (across all sections)
      const hasInvalidBlockHeaders =
        templateType === TemplateType.SINGLE
          ? Object.values(sectionTemplateConfigs).some((config) =>
              some(config.blockHeaders, (blockHeader) => isEmpty(blockHeader)),
            )
          : some(grainBlockHeaders, (grainHeaders) => some(grainHeaders, (grainHeader) => isEmpty(grainHeader)));

      if (hasInvalidBlockHeaders) {
        toastNotification({
          message: 'Please select all Block Header fields, or remove empty Block Headers, before saving.',
          visible: true,
          type: 'warning',
        });
        return;
      }

      // Validate selections based on template type
      if (templateType === TemplateType.SINGLE) {
        // For multi-section, validate all sections have a mapping selected
        const sectionEntries = Object.entries(sectionTemplateConfigs);
        const missingSections = sectionEntries.filter(([, config]) => !config.mapping);
        if (!selectedTemplate || missingSections.length > 0) {
          toastNotification({
            message: 'Please select both Column Template and Column Mapping before proceeding.',
            visible: true,
            type: 'warning',
          });
          return;
        }
      }

      if (templateType === TemplateType.GRANULAR) {
        const missingGrains = reportingPeriods.filter((grain) => !grainTemplates[grain] || !grainMappings[grain]);

        if (missingGrains.length > 0) {
          toastNotification({
            message: `Please select templates and mappings for all grains: ${missingGrains.join(', ')}`,
            visible: true,
            type: 'warning',
          });
          return;
        }
      }

      // Validation passed — handle saving of table configuration before advancing
      // Skip save if no changes exist to persist
      if (!hasTableConfigChanged) {
        setCurrentStep(nextStep);
        return;
      }

      setIsSaving(true);
      saveTemplate(
        templateCRUDItems,
        isUpdateTemplate,
        () => {
          setIsSaving(false);
          setIsUpdateTemplate(true);
          snapshotTableConfig();
          // Update step incomplete status to the next step
          if (tocId) updateTocEntry(tocId, 'group_name', `SETUP_INCOMPLETE|${nextStep}`);
          toastNotification({
            message: 'Table configuration saved successfully.',
            visible: true,
            type: 'success',
          });
          setCurrentStep(nextStep);
        },
        () => {
          setIsSaving(false);
        },
      );
      return;
    }

    // Save Page Details edits when returning to the Page Details step after the initial TOC item and page(s) creation
    if (currentStep === PageSetupSteps.PAGE_DETAILS && isPageCreated && tocEntry) {
      // Internal ID is used for updates as it will differ from toc_id here
      const { internalId } = tocEntry.metadata;
      updateTocEntry(internalId, 'page_name', pageName);
      updateTocEntry(internalId, 'toc_page_name', tocName);
      updateTocEntry(internalId, 'group_name', `SETUP_INCOMPLETE|${nextStep}`);
      setIsSaving(true);
      // Trigger next save of Page Details
      pendingPageDetailsSaveRef.current = true;
      return;
    }

    // Default action to just increment next step
    setCurrentStep(nextStep);
  }, [
    nextStep,
    currentStep,
    isPageCreated,
    tocEntry,
    setCurrentStep,
    templateType,
    grainBlockHeaders,
    templateCRUDItems,
    saveTemplate,
    isUpdateTemplate,
    hasTableConfigChanged,
    snapshotTableConfig,
    toastNotification,
    selectedTemplate,
    sectionTemplateConfigs,
    reportingPeriods,
    grainTemplates,
    grainMappings,
    setIsUpdateTemplate,
    tocId,
    updateTocEntry,
    pageName,
    tocName,
  ]);

  /**
   * Controls the PREVIOUS step progression click.
   * Results in updates to Current and Next steps in the PageSetupContext
   */
  const handlePrevStep = useCallback(() => {
    if (!isNil(currentStep) && currStepIndex > 0) {
      setCurrentStep(pageSetupSteps[currStepIndex - 1]);
    }
  }, [currStepIndex, currentStep, pageSetupSteps, setCurrentStep]);

  /**
   * Handles the Save Page action on the Page Driver step.
   * Persists page driver selections via savePageDrivers, as well as page denominator_label,
   * and displays a toast notification on success or failure.
   */
  const handleSavePage = useCallback(() => {
    // NOTE: Setting prior to success callback to prevent delay/flicker in disabled state of Button.
    setIsRedirecting(true);

    savePageDrivers(
      () => {
        toastNotification({
          message: 'Page drivers saved successfully.',
          visible: true,
          type: 'success',
        });
        // Redirect to Page
        // TODO: Determine if this is needed when modifying existing page.
        navigate(`/deck-preparation/decks/${currDeckId}/pages/${encodeURIComponent(pageName)}`);
      },
      (error) => {
        setIsRedirecting(false);
        toastNotification({
          message: `Unable to save page: ${error.message}`,
          visible: true,
          type: 'error',
        });
      },
    );
  }, [currDeckId, navigate, pageName, savePageDrivers, toastNotification]);

  /**
   * Handles the Create Page action on the Page Details step.
   * Pushes user edits back to TOCContext, persists via SaveDeck, and advances to Table Configuration.
   */
  const handleCreatePage = useCallback(() => {
    if (!tocId || !nextStep) return;

    // Pushes the User edits back to the TOC entry for saving (and to allow capturing of Audit History)
    // Note: On first CREATE tocId is aligned with metadata.internalId for new TOC items (see createNewTOCEntry comment)
    updateTocEntry(tocId, 'page_name', pageName);
    updateTocEntry(tocId, 'toc_page_name', tocName);
    // Update step incomplete status to the next step
    updateTocEntry(tocId, 'group_name', `SETUP_INCOMPLETE|${nextStep}`);

    setIsSaving(true);
    // Trigger a need for Page Details Save
    pendingPageDetailsSaveRef.current = true;
  }, [tocId, nextStep, updateTocEntry, pageName, tocName]);

  // To track whether we are on the Create Page step (New Page flow, Page Details → Table Configuration)
  const isCreatePageStep =
    !isLastStep && isNewPageWorkflow && !isPageCreated && nextStep === PageSetupSteps.TABLE_CONFIGURATION;

  // Redirect to Page Library if the current user does not hold THIS page's lock (editing disabled)
  useEffect(() => {
    if (pageLockStatus.isEditingDisabled) {
      navigate(`/deck-preparation/decks/${currDeckId}/toc`, { replace: true });
    }
  }, [pageLockStatus.isEditingDisabled, currDeckId, navigate]);

  // Deferred Save: UseEffect will run after re-render so that saveChanges has access to the latest
  // modifiedTocEntries when pendingPageDetailsSaveRef is true
  useEffect(() => {
    if (!pendingPageDetailsSaveRef.current) return;
    pendingPageDetailsSaveRef.current = false;
    saveChanges(
      () => {
        setIsSaving(false);
        setIsPageCreated(true);
        toastNotification({
          message: 'Page(s) for reporting periods have been created successfully.',
          visible: true,
          type: 'success',
        });
        setCurrentStep(nextStep!);
      },
      () => {
        setIsSaving(false);
      },
    );
  });

  // Deferred Discard: UseEffect will run after re-render so that saveChanges has access to the
  // deletion-marked modifiedTocEntries when pendingDiscardRef is true
  useEffect(() => {
    if (!pendingDiscardRef.current) return;
    pendingDiscardRef.current = false;
    saveChanges(
      () => navigate(`/deck-preparation/decks/${currDeckId}/toc`),
      () => {
        setIsDiscarding(false);
        toastNotification({
          message: 'Failed to discard changes. Please try again.',
          visible: true,
          type: 'error',
        });
      },
    );
  });

  return (
    <Box className='page-setup-wizard'>
      <Box className='header'>
        <Box className='header-title-section'>
          <Typography variant='h5'>{isNewPageWorkflow ? 'Page Setup - New' : 'Page Setup - Edit'}</Typography>
        </Box>
        <Box className='button-group'>
          <AsyncSaveButton
            inProgress={isDiscarding}
            inProgressContent='Discarding...'
            buttonProps={{
              className: 'step-discard-button',
              variant: 'text',
              size: 'medium',
              disabled: isDiscarding || isSaving,
              onClick: handleDiscard,
            }}
          >
            {isNewPageWorkflow ? 'Discard' : 'Close'}
          </AsyncSaveButton>
          {currStepIndex !== 0 && (
            <Button
              className='step-previous-button'
              variant='outlined'
              size='medium'
              disabled={isSaving || (currentStep === PageSetupSteps.TABLE_CONFIGURATION && hasTableConfigChanged)}
              onClick={handlePrevStep}
            >
              Previous Step
            </Button>
          )}
          {isCreatePageStep && (
            <AsyncSaveButton
              inProgress={isSaving}
              inProgressContent='Creating Page...'
              buttonProps={{
                className: 'step-next-button',
                variant: 'contained',
                color: 'primary',
                size: 'medium',
                disabled: isSaving,
                onClick: handleCreatePage,
              }}
            >
              Create Page
            </AsyncSaveButton>
          )}
          {currentStep === PageSetupSteps.TABLE_CONFIGURATION && hasTableConfigChanged && (
            <Button variant='outlined' size='medium' disabled={isSaving} onClick={resetTableConfig}>
              Discard
            </Button>
          )}
          {!isLastStep && !isCreatePageStep && (
            <AsyncSaveButton
              inProgress={isSaving}
              inProgressContent='Saving...'
              buttonProps={{
                className: 'step-next-button',
                variant: 'contained',
                color: 'primary',
                size: 'medium',
                disabled: isSaving,
                onClick: handleNextStep,
              }}
            >
              {currentStep === PageSetupSteps.TABLE_CONFIGURATION && hasTableConfigChanged
                ? 'Save Config'
                : 'Next Step'}
            </AsyncSaveButton>
          )}
          {isLastStep && (
            <AsyncSaveButton
              inProgress={pageDriversSaving || isRedirecting || isDecklinesLoading}
              inProgressContent={isDecklinesLoading ? 'Loading...' : 'Saving...'}
              buttonProps={{
                className: 'save-page-button',
                variant: 'contained',
                color: 'primary',
                size: 'medium',
                disabled: pageDriversSaving || isRedirecting || isDecklinesLoading,
                onClick: handleSavePage,
              }}
            >
              Save Page
            </AsyncSaveButton>
          )}
        </Box>
      </Box>
      {!isNil(currentStep) && (
        <Box className='page-setup-stepper'>
          {/* Left Stepper — clickable in edit mode to allow direct step navigation */}
          <Stepper activeStep={currStepIndex} orientation='vertical' nonLinear={!isNewPageWorkflow}>
            {map(pageSetupSteps, (step, idx) => (
              <Step key={`${step}-${idx}`} completed={!isNewPageWorkflow && idx !== currStepIndex}>
                {!isNewPageWorkflow ? (
                  <StepButton
                    className='wizard-selectable-step'
                    disabled={isSaving || (currentStep === PageSetupSteps.TABLE_CONFIGURATION && hasTableConfigChanged)}
                    onClick={() => setCurrentStep(step)}
                  >
                    {step}
                  </StepButton>
                ) : (
                  <StepLabel>{step}</StepLabel>
                )}
              </Step>
            ))}
          </Stepper>

          {/* Right Step Content - TODO: Will render individual step components */}
          <Box className='step-content'>
            {currentStep === PageSetupSteps.PAGE_DETAILS && <PageDetails />}
            {currentStep === PageSetupSteps.TABLE_CONFIGURATION && <TableConfiguration />}
            {currentStep === PageSetupSteps.PAGE_DRIVER && <PageDriverStep />}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PageSetupWizard;
